import { all, fork } from 'redux-saga/effects';

import type { SimpleRequestConfig } from 'modules/app/types';
import { simpleRequestsLoader } from 'modules/app/utils';
import type { ApiError, EmptyResponseBody, MetaAction } from 'modules/global/generic.types';
import {
  deletePublicTokenFailure,
  deletePublicTokenSuccess,
  updatePublicTokenStatusFailure,
} from 'modules/publicToken/actions';
import * as types from 'modules/publicToken/actionTypes';
import dashboardSaga from 'modules/publicToken/sagas/dashboard';
import type { DeletePublicTokenAction, PatchPublicTokenStatusAction } from 'modules/publicToken/types';

// The failure callbacks below are declared rather than left to `simpleRequest`: the generated ones
// carry only the caught error, and `ui.deleting` / `ui.patching` need `meta.entityId` to release the
// pending token id.
const simpleRequests: Record<string, SimpleRequestConfig> = {
  [types.CREATE_PUBLIC_TOKEN]: {
    notifications: {
      success: { id: 'page.config.publicToken.create.success', defaultMessage: 'Public token created successfully.' },
      failure: {
        id: 'page.config.publicToken.create.error',
        defaultMessage: 'An error occurred while creating the public token.',
      },
    },
  },
  [types.DELETE_PUBLIC_TOKEN]: {
    notifications: {
      success: { id: 'page.config.publicToken.delete.success', defaultMessage: 'Public token deleted successfully.' },
      failure: {
        id: 'page.config.publicToken.delete.error',
        defaultMessage: 'An error occurred while deleting the public token.',
      },
    },
    callbacks: {
      success: (_: EmptyResponseBody, meta: MetaAction, { payload }: DeletePublicTokenAction) =>
        deletePublicTokenSuccess(payload.publicTokenId, meta),
      failure: (error: ApiError, meta: MetaAction, { payload }: DeletePublicTokenAction) =>
        deletePublicTokenFailure(error, { ...meta, entityId: payload.publicTokenId }),
    },
  },
  [types.UPDATE_PUBLIC_TOKEN_STATUS]: {
    notifications: {
      success: { id: 'page.config.publicToken.update.success', defaultMessage: 'Public token updated successfully.' },
      failure: {
        id: 'page.config.publicToken.update.error',
        defaultMessage: 'An error occurred while updating the public token.',
      },
    },
    callbacks: {
      failure: (error: ApiError, meta: MetaAction, { payload }: PatchPublicTokenStatusAction) =>
        updatePublicTokenStatusFailure(error, { ...meta, entityId: payload.publicTokenId }),
    },
  },
};

export default function* publicTokensSaga() {
  yield all(simpleRequestsLoader(simpleRequests));
  yield fork(dashboardSaga);
}
